import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import type { DatePickerProps, DateRange } from './types';
import { computeCalendarPosition } from './calendar-position';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const DAY_NAMES = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
}

function isBeforeDay(a: Date, b: Date): boolean {
  return startOfDay(a) < startOfDay(b);
}

function isAfterDay(a: Date, b: Date): boolean {
  return startOfDay(a) > startOfDay(b);
}

function formatDate(d: Date): string {
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

/** Returns the calendar grid cells (null = empty padding cell) */
function buildCalendarGrid(year: number, month: number): (number | null)[] {
  const firstDay = new Date(year, month, 1).getDay();
  const total = getDaysInMonth(year, month);
  const cells: (number | null)[] = Array(firstDay).fill(null);
  for (let d = 1; d <= total; d++) cells.push(d);
  // Pad to full rows
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

// Year-grid drill-down (regression #86): without this, navigating from
// "April 2026" back to a 1990 birthday required ~432 single-month clicks.
// We render a 12-year window centred on the current view year so any
// historical date (or future anniversary) is two clicks away.
export function buildYearGrid(centerYear: number): number[] {
  const start = centerYear - (centerYear % 12);
  return Array.from({ length: 12 }, (_, i) => start + i);
}

function buildDefaultPresets(): { label: string; value: DateRange }[] {
  const today = startOfDay(new Date());
  const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
  const last7Start = new Date(today); last7Start.setDate(today.getDate() - 6);
  const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
  return [
    { label: 'Today', value: { from: today, to: today } },
    { label: 'Yesterday', value: { from: yesterday, to: yesterday } },
    { label: 'Last 7 days', value: { from: last7Start, to: today } },
    { label: 'This month', value: { from: thisMonthStart, to: today } },
    { label: 'Last month', value: { from: lastMonthStart, to: lastMonthEnd } },
  ];
}

// ─── CalendarIcon ─────────────────────────────────────────────────────────────

function CalendarIcon() {
  return (
    <svg
      className="h-4 w-4 text-neutral-400 shrink-0"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}

function ChevronIcon({ direction }: { direction: 'left' | 'right' }) {
  return (
    <svg
      className="h-4 w-4"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {direction === 'left'
        ? <polyline points="15 18 9 12 15 6" />
        : <polyline points="9 18 15 12 9 6" />}
    </svg>
  );
}

// ─── DatePicker ───────────────────────────────────────────────────────────────

export function DatePicker(props: DatePickerProps) {
  const {
    variant = 'single',
    minDate,
    maxDate,
    label,
    placeholder = 'DD/MM/YYYY',
    error,
    disabled = false,
  } = props;

  const id = useId();
  const errorId = `${id}-error`;
  const calendarId = `${id}-calendar`;

  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);

  // Portal coordinates — computed from the trigger's bounding rect so the
  // calendar can escape parent stacking contexts (e.g. the filter bar's
  // backdrop-blur) that would otherwise trap its z-index. See issue #79.
  const [calendarPos, setCalendarPos] = useState<{
    top: number;
    left: number;
    maxHeight: number;
  } | null>(null);

  const updateCalendarPosition = useCallback(() => {
    if (!triggerRef.current || typeof window === 'undefined') return;
    const rect = triggerRef.current.getBoundingClientRect();
    const el = calendarRef.current;
    const size = {
      width: el?.offsetWidth ?? 280,
      // scrollHeight (not offsetHeight) so the placement decision uses the
      // calendar's natural content height even after a previous pass has
      // already applied a constraining max-height. See issue #80.
      height: el?.scrollHeight ?? 340,
    };
    setCalendarPos(
      computeCalendarPosition(
        { top: rect.top, bottom: rect.bottom, left: rect.left },
        size,
        { width: window.innerWidth, height: window.innerHeight },
      ),
    );
  }, []);

  // Anchor the portalled calendar to the trigger and keep it anchored on
  // scroll / resize while open.
  useLayoutEffect(() => {
    if (!open) return;
    updateCalendarPosition();
    window.addEventListener('resize', updateCalendarPosition);
    window.addEventListener('scroll', updateCalendarPosition, true);
    return () => {
      window.removeEventListener('resize', updateCalendarPosition);
      window.removeEventListener('scroll', updateCalendarPosition, true);
    };
  }, [open, updateCalendarPosition]);

  // Calendar view state
  const today = startOfDay(new Date());
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  // Drill-down view: clicking the header steps day → month → year so the
  // user can jump to e.g. August 1990 in two clicks instead of 432.
  const [view, setView] = useState<'day' | 'month' | 'year'>('day');
  // For range: track in-progress selection
  const [rangeAnchor, setRangeAnchor] = useState<Date | null>(null);
  // Focused date in calendar grid for keyboard navigation
  const [focusedDate, setFocusedDate] = useState<Date>(today);

  // Sync view to selected value when opening
  useEffect(() => {
    if (!open) return;
    if (variant === 'single' && props.value && props.value instanceof Date) {
      setViewYear(props.value.getFullYear());
      setViewMonth(props.value.getMonth());
      setFocusedDate(props.value);
    } else if (variant === 'range' && props.value && !(props.value instanceof Date) && props.value.from) {
      setViewYear(props.value.from.getFullYear());
      setViewMonth(props.value.from.getMonth());
      setFocusedDate(props.value.from);
    } else {
      setFocusedDate(today);
    }
    setRangeAnchor(null);
    setView('day');
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (
        calendarRef.current && !calendarRef.current.contains(e.target as Node) &&
        triggerRef.current && !triggerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Focus first focusable cell when calendar opens
  useEffect(() => {
    if (open && view === 'day' && calendarRef.current) {
      const cell = calendarRef.current.querySelector<HTMLElement>('[data-day]');
      cell?.focus();
    }
  }, [open, view]);

  function prevMonth() {
    if (viewMonth === 0) { setViewYear(y => y - 1); setViewMonth(11); }
    else setViewMonth(m => m - 1);
  }
  function nextMonth() {
    if (viewMonth === 11) { setViewYear(y => y + 1); setViewMonth(0); }
    else setViewMonth(m => m + 1);
  }

  function headerNavPrev() {
    if (view === 'day') prevMonth();
    else if (view === 'month') setViewYear(y => y - 1);
    else setViewYear(y => y - 12);
  }
  function headerNavNext() {
    if (view === 'day') nextMonth();
    else if (view === 'month') setViewYear(y => y + 1);
    else setViewYear(y => y + 12);
  }
  function cycleView() {
    setView(v => (v === 'day' ? 'month' : v === 'month' ? 'year' : 'day'));
  }

  function isDisabledDate(d: Date): boolean {
    if (minDate && isBeforeDay(d, minDate)) return true;
    if (maxDate && isAfterDay(d, maxDate)) return true;
    return false;
  }

  function isSelected(d: Date): boolean {
    if (variant === 'single') {
      return Boolean(props.value && isSameDay(d, props.value as Date));
    }
    const range = props.value as DateRange | undefined;
    if (!range) return false;
    return isSameDay(d, range.from) || isSameDay(d, range.to);
  }

  function isInRange(d: Date): boolean {
    if (variant !== 'range') return false;
    const range = props.value as DateRange | undefined;
    if (!range) return false;
    return isAfterDay(d, range.from) && isBeforeDay(d, range.to);
  }

  function handleDayClick(d: Date) {
    if (isDisabledDate(d)) return;

    if (variant === 'single') {
      (props as { onChange?: (v: Date) => void }).onChange?.(d);
      setOpen(false);
      triggerRef.current?.focus();
      return;
    }

    // Range selection: first click = anchor, second click = complete
    if (!rangeAnchor) {
      setRangeAnchor(d);
    } else {
      const from = isBeforeDay(d, rangeAnchor) ? d : rangeAnchor;
      const to = isBeforeDay(d, rangeAnchor) ? rangeAnchor : d;
      (props as { onChange?: (v: DateRange) => void }).onChange?.({ from, to });
      setRangeAnchor(null);
      setOpen(false);
      triggerRef.current?.focus();
    }
  }

  function handleCalendarKeyDown(e: React.KeyboardEvent) {
    if (view !== 'day') {
      if (e.key === 'Escape') { e.preventDefault(); setOpen(false); triggerRef.current?.focus(); }
      return;
    }
    const next = new Date(focusedDate);
    switch (e.key) {
      case 'ArrowLeft':  e.preventDefault(); next.setDate(next.getDate() - 1); break;
      case 'ArrowRight': e.preventDefault(); next.setDate(next.getDate() + 1); break;
      case 'ArrowUp':    e.preventDefault(); next.setDate(next.getDate() - 7); break;
      case 'ArrowDown':  e.preventDefault(); next.setDate(next.getDate() + 7); break;
      case 'PageUp':     e.preventDefault(); next.setMonth(next.getMonth() - 1); break;
      case 'PageDown':   e.preventDefault(); next.setMonth(next.getMonth() + 1); break;
      case 'Home':       e.preventDefault(); next.setDate(next.getDate() - next.getDay()); break;
      case 'End':        e.preventDefault(); next.setDate(next.getDate() + (6 - next.getDay())); break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        handleDayClick(focusedDate);
        return;
      case 'Escape':
        e.preventDefault();
        setOpen(false);
        triggerRef.current?.focus();
        return;
      default: return;
    }
    setFocusedDate(next);
    setViewYear(next.getFullYear());
    setViewMonth(next.getMonth());
    // Focus the new cell
    setTimeout(() => {
      const cell = calendarRef.current?.querySelector<HTMLElement>(`[data-day="${next.toISOString().slice(0, 10)}"]`);
      cell?.focus();
    }, 0);
  }

  // Display string for trigger button
  function displayValue(): string {
    if (variant === 'single') {
      const v = props.value as Date | undefined;
      return v ? formatDate(v) : '';
    }
    const v = props.value as DateRange | undefined;
    if (!v) return '';
    return isSameDay(v.from, v.to)
      ? formatDate(v.from)
      : `${formatDate(v.from)} – ${formatDate(v.to)}`;
  }

  const hasError = Boolean(error);
  const presets = variant === 'range'
    ? ((props as { presets?: { label: string; value: DateRange }[] }).presets ?? buildDefaultPresets())
    : [];

  const cells = buildCalendarGrid(viewYear, viewMonth);

  const triggerClasses = [
    'flex items-center gap-2 w-full border rounded bg-white h-11 px-3 text-sm transition-colors duration-150',
    'dark:bg-neutral-800 dark:text-neutral-100',
    hasError
      ? 'border-danger-500 focus:ring-2 focus:ring-danger-500 focus:ring-offset-0 focus:outline-none'
      : 'border-neutral-300 dark:border-neutral-600 hover:border-neutral-400 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 focus:ring-offset-0 focus:outline-none',
    disabled ? 'bg-neutral-100 dark:bg-neutral-700 opacity-70 cursor-not-allowed pointer-events-none' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className="flex flex-col gap-1 relative">
      {label && (
        <label
          htmlFor={`${id}-trigger`}
          className={[
            'text-sm font-medium leading-none',
            hasError ? 'text-danger-700 dark:text-danger-400' : 'text-neutral-700 dark:text-neutral-300',
          ].join(' ')}
        >
          {label}
        </label>
      )}

      <button
        ref={triggerRef}
        id={`${id}-trigger`}
        type="button"
        className={triggerClasses}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={calendarId}
        aria-describedby={hasError ? errorId : undefined}
        aria-invalid={hasError || undefined}
        onClick={() => !disabled && setOpen(o => !o)}
      >
        <CalendarIcon />
        <span className={displayValue() ? 'text-neutral-900 dark:text-neutral-100' : 'text-neutral-400'}>
          {displayValue() || placeholder}
        </span>
      </button>

      {hasError && (
        <span id={errorId} role="alert" className="text-xs text-danger-600 dark:text-danger-400">
          {error}
        </span>
      )}

      {/* Calendar popup — portalled to <body> so it escapes parent stacking
          contexts (e.g. backdrop-blur filter bars) that would otherwise
          trap its z-index below later cards. See issue #79. */}
      {open && typeof document !== 'undefined' && createPortal(
        <div
          ref={calendarRef}
          id={calendarId}
          role="dialog"
          aria-modal="true"
          aria-label={label ?? 'Select date'}
          style={{
            position: 'fixed',
            top: calendarPos?.top ?? 0,
            left: calendarPos?.left ?? 0,
            maxHeight: calendarPos?.maxHeight,
            visibility: calendarPos ? 'visible' : 'hidden',
          }}
          className="z-[9999] overflow-y-auto bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-lg shadow-lg flex"
          onKeyDown={handleCalendarKeyDown}
        >
          {/* Presets column (range only) */}
          {presets.length > 0 && (
            <div className="flex flex-col gap-0.5 p-2 border-r border-neutral-200 dark:border-neutral-700 min-w-[120px]">
              {presets.map((preset) => (
                <button
                  key={preset.label}
                  type="button"
                  className="text-left text-sm px-3 py-1.5 rounded hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-700 dark:text-neutral-300 transition-colors"
                  onClick={() => {
                    (props as { onChange?: (v: DateRange) => void }).onChange?.(preset.value);
                    setOpen(false);
                    triggerRef.current?.focus();
                  }}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          )}

          {/* Calendar grid */}
          <div className="p-3 w-[280px]">
            {/* Month/Year navigation */}
            <div className="flex items-center justify-between mb-3">
              <button
                type="button"
                className="p-1 rounded hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-600 dark:text-neutral-300 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                onClick={headerNavPrev}
                aria-label={view === 'day' ? 'Previous month' : view === 'month' ? 'Previous year' : 'Previous decade'}
              >
                <ChevronIcon direction="left" />
              </button>
              <button
                type="button"
                onClick={cycleView}
                aria-label="Switch calendar view"
                className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 px-2 py-1 rounded hover:bg-neutral-100 dark:hover:bg-neutral-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
              >
                {view === 'day' && `${MONTH_NAMES[viewMonth]} ${viewYear}`}
                {view === 'month' && `${viewYear}`}
                {view === 'year' && (() => {
                  const yrs = buildYearGrid(viewYear);
                  return `${yrs[0]} – ${yrs[yrs.length - 1]}`;
                })()}
              </button>
              <button
                type="button"
                className="p-1 rounded hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-600 dark:text-neutral-300 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                onClick={headerNavNext}
                aria-label={view === 'day' ? 'Next month' : view === 'month' ? 'Next year' : 'Next decade'}
              >
                <ChevronIcon direction="right" />
              </button>
            </div>

            {view === 'year' && (
              <div role="grid" aria-label="Select year" className="grid grid-cols-3 gap-2">
                {buildYearGrid(viewYear).map((yr) => {
                  const minY = minDate?.getFullYear();
                  const maxY = maxDate?.getFullYear();
                  const yrDisabled = (minY !== undefined && yr < minY) || (maxY !== undefined && yr > maxY);
                  const isCurrent = yr === viewYear;
                  return (
                    <button
                      key={yr}
                      type="button"
                      disabled={yrDisabled}
                      onClick={() => { setViewYear(yr); setView('month'); }}
                      className={[
                        'h-10 rounded text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
                        yrDisabled
                          ? 'text-neutral-300 dark:text-neutral-600 cursor-not-allowed'
                          : isCurrent
                            ? 'bg-brand-500 text-white font-semibold'
                            : 'text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800',
                      ].join(' ')}
                    >
                      {yr}
                    </button>
                  );
                })}
              </div>
            )}

            {view === 'month' && (
              <div role="grid" aria-label="Select month" className="grid grid-cols-3 gap-2">
                {MONTH_NAMES.map((name, idx) => {
                  const monthStart = new Date(viewYear, idx, 1);
                  const monthEnd = new Date(viewYear, idx + 1, 0);
                  const mDisabled = (minDate ? isBeforeDay(monthEnd, minDate) : false)
                    || (maxDate ? isAfterDay(monthStart, maxDate) : false);
                  const isCurrent = idx === viewMonth;
                  return (
                    <button
                      key={name}
                      type="button"
                      disabled={mDisabled}
                      onClick={() => { setViewMonth(idx); setView('day'); }}
                      className={[
                        'h-10 rounded text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
                        mDisabled
                          ? 'text-neutral-300 dark:text-neutral-600 cursor-not-allowed'
                          : isCurrent
                            ? 'bg-brand-500 text-white font-semibold'
                            : 'text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800',
                      ].join(' ')}
                    >
                      {name.slice(0, 3)}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Day header */}
            {view === 'day' && <div role="grid" aria-label={`${MONTH_NAMES[viewMonth]} ${viewYear}`}>
              <div role="row" className="grid grid-cols-7 mb-1">
                {DAY_NAMES.map((d) => (
                  <div
                    key={d}
                    role="columnheader"
                    aria-label={d}
                    className="text-center text-xs font-medium text-neutral-500 dark:text-neutral-400 py-1"
                  >
                    {d}
                  </div>
                ))}
              </div>

              {/* Weeks */}
              {Array.from({ length: cells.length / 7 }, (_, week) => (
                <div key={week} role="row" className="grid grid-cols-7">
                  {cells.slice(week * 7, week * 7 + 7).map((day, col) => {
                    if (day === null) {
                      return <div key={col} role="gridcell" aria-hidden="true" />;
                    }
                    const date = new Date(viewYear, viewMonth, day);
                    const isToday = isSameDay(date, today);
                    const selected = isSelected(date);
                    const inRange = isInRange(date);
                    const disabledDay = isDisabledDate(date);
                    const isFocused = isSameDay(date, focusedDate);
                    const dateKey = date.toISOString().slice(0, 10);

                    return (
                      <div key={col} role="gridcell">
                        <button
                          type="button"
                          data-day={dateKey}
                          tabIndex={isFocused ? 0 : -1}
                          disabled={disabledDay}
                          aria-selected={selected}
                          aria-label={formatDate(date)}
                          aria-current={isToday ? 'date' : undefined}
                          onClick={() => handleDayClick(date)}
                          onFocus={() => setFocusedDate(date)}
                          className={[
                            'w-full aspect-square flex items-center justify-center text-sm rounded transition-colors',
                            'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
                            disabledDay
                              ? 'text-neutral-300 dark:text-neutral-600 cursor-not-allowed'
                              : selected
                                ? 'bg-brand-500 text-white font-semibold'
                                : inRange
                                  ? 'bg-brand-100 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300'
                                  : isToday
                                    ? 'border border-brand-500 text-brand-600 dark:text-brand-400 hover:bg-brand-50 dark:hover:bg-brand-900/20'
                                    : 'text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800',
                          ].filter(Boolean).join(' ')}
                        >
                          {day}
                        </button>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
